<template>
  <div class="base-input-root">
    <div class="base-input-wrapper">
      <div :class="[`base-input--type-${type}`]" class="base-input">
        <div v-if="$slots.prepend" class="base-input__before">
          <slot name="prepend" />
        </div>
        <textarea
          v-if="type === 'textarea'"
          :id="inputId"
          ref="field"
          v-bind="$attrs"
          :type="inputType"
          :class="classNames"
          :value="value"
          v-on="listeners"
          @input="handleUpdate"
          @focus="$emit('focus', $refs.field)"
          @blur="$emit('blur', $refs.field)"
        />
        <input
          v-else-if="type !== 'file'"
          :id="inputId"
          ref="field"
          v-bind="$attrs"
          :type="inputType"
          :class="classNames"
          :value="value"
          v-on="listeners"
          @input="handleUpdate"
          @keypress="handleKeyPressUpdate"
        />
        <input
          v-if="type === 'file'"
          :id="inputId"
          ref="field"
          :key="fileRecreateKey"
          v-bind="$attrs"
          type="file"
          class="base-input__file-control"
          v-on="listeners"
          @focus="$emit('focus', $refs.field)"
          @blur="$emit('blur', $refs.field)"
          @change="handleFileChange"
        />
        <label v-if="type === 'file'" :for="inputId" :class="classNames">
          <span v-if="filenames">{{ filenames }}</span>
          <span v-else class="file-placeholder">{{
            $attrs.placeholder || "Приложите файл"
          }}</span>
        </label>

        <div v-if="isPassword" class="base-input__after">
          <div v-if="isPassword" class="pwd-toggle" @click="handleChangeType">
            <BaseIcon
              :title="
                currentType === 'text' ? 'Скрыть пароль' : 'Показать пароль'
              "
              :class="`pwd-toggle__icon--${currentType}`"
              :glyph="currentType === 'text' ? 'eye' : 'eye-close'"
              class="pwd-toggle__icon"
              width="20px"
            />
          </div>
        </div>
        <div v-else class="base-input__after">
          <slot v-bind="{ handleResetFile }" name="append" />
        </div>
      </div>
    </div>
    <span v-if="errors && errors.length" class="form-error">{{
      errors[0]
    }}</span>
  </div>
</template>

<script>
export default {
  name: "BaseInput",
  inheritAttrs: false,
  props: {
    keysAllowed: Array,
    type: {
      type: String,
      default: "text",
    },
    errors: {
      type: Array,
      default: () => [],
    },
    // Default, Second
    theme: {
      type: String,
      default: "default",
    },
    togglePassword: {
      type: Boolean,
      default: false,
    },
    id: {
      type: [String, Number],
    },
    // Опции маски для ввода
    // @see https://nosir.github.io/cleave.js/
    format: {
      type: Object,
      default: null,
    },
    /**
     * @type String
     * @defaults md
     */
    size: {
      type: String,
      default: "md",
    },
    // Отправлять неформатированное value
    raw: {
      type: Boolean,
      default: false,
    },
    valid: Boolean,
    // eslint-disable-next-line vue/require-prop-types
    value: {},
  },
  data() {
    return {
      currentType: "password",
      filenames: null,
      isPassword: this.type === "password",
      fileRecreateKey: 1,
      innerId: "app-input-" + Date.now(),
    };
  },
  computed: {
    hasAppend() {
      const { $slots, $scopedSlots } = this;
      if ($slots.append) return true;
      if ($scopedSlots.append && typeof $scopedSlots.append === "function") {
        const content = $scopedSlots.append();
        return Boolean(content && content.length > 0);
      }
      return false;
    },
    hasPrepend() {
      const { $slots, $scopedSlots } = this;
      if ($slots.prepend) return true;
      if ($scopedSlots.prepend && typeof $scopedSlots.prepend === "function") {
        const content = $scopedSlots.prepend();
        return Boolean(content && content.length > 0);
      }
      return false;
    },
    inputType() {
      const { type, isPassword, currentType } = this;
      return isPassword ? currentType : type;
    },
    listeners() {
      /* eslint-disable no-unused-vars */
      const { input, ...rest } = this.$listeners;
      /* eslint-enable no-unused-vars */
      return rest;
    },
    classNames() {
      const baseClass = "base-input__control";
      const {
        togglePassword,
        errors,
        type,
        theme,
        valid,
        size,
        hasPrepend,
        hasAppend,
      } = this;
      return [
        baseClass,
        `${baseClass}--type-${type}`,
        `${baseClass}--size-${size}`,
        `${baseClass}--theme-${theme}`,
        {
          [`${baseClass}--has-toggle`]: togglePassword,
          [`${baseClass}--has-error`]: errors.length,
          [`${baseClass}--with-append`]: hasAppend,
          [`${baseClass}--with-prepend`]: hasPrepend,
          [`${baseClass}--is-valid`]: valid,
        },
      ];
    },
    inputId() {
      return this.id || this.innerId;
    },
  },
  mounted() {
    /* global Cleave */
    const { togglePassword, type } = this;
    if (togglePassword) {
      this.currentType = type;
    }

    if (this.format) {
      const input = this.$refs.field;
      this.cleave = new Cleave(input, {
        ...this.format,
        onValueChanged: this.handleFormatValueUpdate.bind(this),
      });
      if (this.value) {
        this.cleave.setRawValue(this.value);
      }
    }
  },

  created() {
    this.focus = () => {
      this.$refs.field.focus();
    };
  },

  beforeDestroy() {
    if (this.cleave) {
      this.cleave.destroy();
      this.cleave = null;
    }
  },
  methods: {
    handleFormatValueUpdate(e) {
      this.$nextTick(() => {
        let val = this.raw ? this.cleave.getRawValue() : e.target.value;
        this.$emit("input", val);
      });
    },
    handleUpdate(e) {
      if (this.format) return;
      if (this.type === "file") {
        this.$emit("files", e.target.files);
      } else {
        this.$emit("input", e.target.value);
      }
    },
    handleKeyPressUpdate(e) {
      if (this.keysAllowed?.length) {
        if (!this.keysAllowed.includes(e.key)) {
          e.preventDefault();
        } else {
          this.$emit("keypress", e.key);
        }
      } else {
        this.$emit("keypress", e.key);
      }
    },
    handleFileChange(e) {
      this.$emit("input", e);
      this.getFileNames();
    },
    handleResetFile() {
      this.$emit("reset", this.$refs.field);
      // Хак для того чтобы сбросить с нативного инпута
      // value с файлом
      this.$refs.field.type = "text";
      this.$refs.field.type = "file";
      this.handleFileChange(null);
    },
    handleChangeType() {
      this.currentType = this.currentType === "text" ? "password" : "text";
    },
    getFileNames() {
      const field = this.$refs.field;
      if (!field) return;
      if (field.files.length) {
        this.filenames = [...field.files].map((n) => n.name).join(", ");
      } else {
        this.filenames = "";
      }
    },
  },
};
</script>

<style lang="less" scoped>
.base-input {
  width: 100%;

  &-wrapper {
    position: relative;
  }

  &__file-control {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    left: -1px;
    opacity: 0;
    z-index: -1;
  }

  &__control {
    width: 100%;
    border: @input-border-width solid @input-border-color;
    height: @input-height;
    padding: 0 @input-padding;
    box-shadow: none;
    font-size: @input-font-size;
    font-family: inherit;
    border-radius: @input-border-radius;
    transition: border-color 0.3s;
    background-color: @input-bg-color;
    display: block;
    box-sizing: border-box;
    color: @input-text-color;
    appearance: none;

    &:hover {
      border-color: @input-hover-border-color;
    }

    &:focus {
      border-color: @input-focus-border-color;
      background-color: @input-focus-bg-color;
      outline: none;
    }

    &::placeholder {
      /* Chrome, Firefox, Opera, Safari 10.1+ */
      color: @input-placeholder-color;
      opacity: 1; /* Firefox */
    }
    &:-ms-input-placeholder {
      /* Internet Explorer 10-11 */
      color: @input-placeholder-color;
    }
    &::-ms-input-placeholder {
      /* Microsoft Edge */
      color: @input-placeholder-color;
    }

    &--type-password {
      padding-right: 40px;
    }
    &--type-textarea {
      height: auto;
      resize: vertical;
      padding-top: @input-padding;
      padding-bottom: @input-padding;
      line-height: 1.32;
      min-height: @input-height;
    }
    &--type-file {
      > span {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        width: 100%;
        display: inline-block;
      }
    }

    &--with-prepend {
      padding-left: @input-padding + @input-prepend-width;
    }

    &--with-append {
      padding-right: @input-padding + @input-append-width;
    }

    &--is-valid {
      border-color: @input-success-border-color !important;
    }

    &--has-error {
      border-color: @input-error-border-color !important;
    }

    &:disabled {
      background-color: #fff !important;
      border-color: @input-disabled-color !important;
      color: @input-disabled-color;

      &::placeholder {
        color: @input-disabled-color;
      }

      &:-ms-input-placeholder {
        color: @input-disabled-color;
      }

      &::-ms-input-placeholder {
        color: @input-disabled-color;
      }
    }

    &--theme-second {
      background-color: @filter-bg-color;
      color: @filter-text-color;
      padding: 0 @filter-padding;
      border: @filter-border-width solid @filter-border-color;

      &::placeholder {
        color: @filter-text-color;
      }

      &:-ms-input-placeholder {
        color: @filter-text-color;
      }

      &::-ms-input-placeholder {
        color: @filter-text-color;
      }

      &:hover {
        border-color: @filter-hover-border-color;
      }

      &:disabled {
        border-color: @filter-disabled-color !important;
        color: @filter-disabled-color;
        background-color: @filter-bg-color !important;

        &::placeholder {
          color: @filter-disabled-color;
        }

        &:-ms-input-placeholder {
          color: @filter-disabled-color;
        }

        &::-ms-input-placeholder {
          color: @filter-disabled-color;
        }
      }
    }
  }

  &__after {
    position: absolute;
    top: 0;
    bottom: 0;
    right: @input-padding * 0.5;
    display: flex;
    align-items: center;
    justify-content: center;
    width: @input-append-width;
    font-size: 16px;

    &:empty {
      display: none;
    }
  }

  &__before {
    position: absolute;
    top: 0;
    bottom: 0;
    left: @input-padding * 0.5;
    display: flex;
    align-items: center;
    justify-content: center;
    width: @input-prepend-width;
    &:empty {
      display: none;
    }
  }

  &--type-number &__control {
    -moz-appearance: textfield;
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }

  &--type-file &__control {
    line-height: @input-height;
  }

  .pwd-toggle {
    user-select: none;
    &__icon {
      color: @primary-color;
      cursor: pointer;

      &--password {
        color: @gray-color;
      }
    }
  }

  .file-placeholder {
    color: @input-placeholder-color;
  }

  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus {
    -webkit-text-fill-color: @input-text-color;
    border-color: @input-focus-border-color !important;
    box-shadow: 0 0 0 1000px @link-color inset;
    transition: background-color 5000s ease-in-out 0s;
  }
}
</style>
